import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import type { MeterUploadRowData } from "@utils/bulkMeterUpload/buildTrialRunRow";
import {
  applyOfftakeSelection,
  cascadeWouldOverwrite,
  resolveCurrentOption,
  toOfftakeOptions,
  type ExtractionPointLookupRecord,
  type OfftakeOption,
} from "@utils/bulkMeterUpload/offtakeSelect";

type OfftakeSelectCellProps = {
  rowData: MeterUploadRowData;
  // The scheme's extraction points, fetched once by TrialRun and shared across rows.
  // The cell filters them to its own row's subscriber (toOfftakeOptions keys on the
  // account number), so options differ per row — one lookup, per-row filtering.
  extractionPoints: ExtractionPointLookupRecord[] | undefined;
  // Non-ledgered rows can re-select; ledgered rows render the offtake read-only.
  editable: boolean;
  // SEQWATER also cascades group + sequence (properties of the extraction point);
  // other schemes cascade only the meter serial.
  isSeqwater: boolean;
  // Commits the cascaded rowData through the same path as the value cells
  // (useBulkMeterUploadFlow.editRow). extractionPointName / serialNo / group / seq are
  // all in trialRunSig, so a selection re-runs the trial.
  onCommit: (rowData: MeterUploadRowData) => void | Promise<void>;
};

// The offtake cell as a subscriber-filtered, scheme-scoped select. Picking an offtake
// cascades the meter serial (and, for SEQWATER, group + sequence) onto the row. When
// that cascade would replace a DIFFERENT existing value the cell asks first ("overwrite,
// but confirm first"); empty cells (or values that already match) fill silently. A
// parsed/draft offtake with no lookup match still renders as the current value and can
// be replaced (resolveCurrentOption returns a synthetic option).
const OfftakeSelectCell: React.FunctionComponent<OfftakeSelectCellProps> = ({
  rowData,
  extractionPoints,
  editable,
  isSeqwater,
  onCommit,
}) => {
  const { t } = useTranslation();
  // A selection held back pending the operator's confirmation to overwrite.
  const [pending, setPending] = React.useState<OfftakeOption | null>(null);

  const options = React.useMemo(
    () => toOfftakeOptions(extractionPoints, rowData.accountNumber),
    [extractionPoints, rowData.accountNumber],
  );

  if (!editable) {
    return <span>{rowData.extractionPointName}</span>;
  }

  const current = resolveCurrentOption(rowData.extractionPointName, options);

  const handleChange = (option: OfftakeOption | null) => {
    if (option && cascadeWouldOverwrite(rowData, option, isSeqwater)) {
      // Replacing a different existing serial / group / sequence — confirm first.
      setPending(option);
      return;
    }
    onCommit(applyOfftakeSelection(rowData, option, isSeqwater));
  };

  return (
    <div className="flex flex-col gap-1">
      <Select<OfftakeOption>
        classNamePrefix="offtake_select"
        className="min-w-[12rem] text-sm"
        placeholder={t("bulk_meter_upload.offtake.placeholder")}
        noOptionsMessage={() => t("bulk_meter_upload.offtake.no_options")}
        options={options}
        value={current}
        onChange={handleChange}
        isClearable
        aria-label={t("common.extraction_point") || undefined}
        // Portal the menu out of the scrolling table so it isn't clipped by the cell.
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        menuPosition="fixed"
      />
      {pending ? (
        <div
          data-offtake-confirm
          className="rounded border border-yellow-300 bg-yellow-50 p-2 text-xs"
        >
          <p className="mb-1">
            {t("bulk_meter_upload.offtake.overwrite_confirm", {
              name: pending.name,
            })}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-primary text-xs rounded px-2 py-1"
              onClick={() => {
                onCommit(applyOfftakeSelection(rowData, pending, isSeqwater));
                setPending(null);
              }}
            >
              {t("bulk_meter_upload.offtake.overwrite_replace")}
            </button>
            <button
              type="button"
              className="btn-outline-primary text-xs rounded px-2 py-1"
              onClick={() => setPending(null)}
            >
              {t("bulk_meter_upload.offtake.overwrite_keep")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OfftakeSelectCell;
